import { motion } from "framer-motion";
import { ClipboardList, Wrench, Radar, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/App";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
    {
        num: "01",
        icon: ClipboardList,
        title: "Request & tailor",
        text: "Tell us about your office — headcount, tastes, dietary needs. We build a snack lineup your team actually wants and schedule a free site visit.",
        meta: "5-minute form",
    },
    {
        num: "02",
        icon: Wrench,
        title: "Zero-cost install",
        text: "Our white-glove crew delivers, installs, and stocks your machine within about 48 hours of approval. No fees, no lease, no contract — we earn from what sells.",
        meta: "~48 hour turnaround",
    },
    {
        num: "03",
        icon: Radar,
        title: "Telemetry restocking",
        text: "Sensors watch every coil in real time. We restock before items run out, rotate in new products based on what your team loves, and service everything ourselves.",
        meta: "Monitored 24/7",
    },
];

export default function Process() {
    return (
        <section id="how-it-works" data-testid="process-section" className="relative py-24 sm:py-32 bg-[#0E1E12] overflow-hidden">
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(166,206,57,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(166,206,57,0.05) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                }}
            />
            <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-[#3FAE4A]/15 blur-[120px] pointer-events-none" aria-hidden />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-16">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#A6CE39] mb-5"
                        data-testid="process-eyebrow"
                    >
                        How it works
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-[#F9F8F3]"
                        data-testid="process-heading"
                    >
                        Three chapters. <span className="text-[#A6CE39]">Zero invoices.</span>
                    </motion.h2>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {CHAPTERS.map((c, i) => (
                        <motion.article
                            key={c.num}
                            data-testid={`how-it-works-chapter-${c.num}`}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, delay: i * 0.15, ease: EASE }}
                            className={`group relative rounded-3xl border border-[#A6CE39]/15 bg-[#122A16]/70 backdrop-blur p-8 transition-[border-color,box-shadow,transform] duration-500 hover:border-[#A6CE39]/45 hover:shadow-[0_0_50px_-12px_rgba(166,206,57,0.25)] hover:-translate-y-1.5 ${
                                i === 1 ? "md:translate-y-10" : ""
                            }`}
                        >
                            <div className="flex items-start justify-between mb-10">
                                <span className="font-mono-brand text-6xl font-semibold text-transparent bg-clip-text bg-gradient-to-b from-[#A6CE39] to-[#A6CE39]/10 leading-none">
                                    {c.num}
                                </span>
                                <div className="h-11 w-11 rounded-2xl bg-[#A6CE39]/10 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#A6CE39]/20">
                                    <c.icon className="h-5 w-5 text-[#A6CE39]" />
                                </div>
                            </div>
                            <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#F9F8F3] tracking-tight">
                                {c.title}
                            </h3>
                            <p className="mt-3 text-sm text-[#F9F8F3]/65 leading-relaxed">{c.text}</p>
                            <p className="mt-6 font-mono-brand text-[11px] tracking-[0.2em] uppercase text-[#F58220]">
                                {c.meta}
                            </p>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
                    className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-3xl border border-[#A6CE39]/15 bg-[#A6CE39]/5 p-8"
                    data-testid="process-cta-band"
                >
                    <p className="font-display text-xl sm:text-2xl font-bold text-[#F9F8F3] tracking-tight">
                        Ready when you are —{" "}
                        <span className="text-[#A6CE39]">most machines are live within a week.</span>
                    </p>
                    <button
                        data-testid="process-cta-btn"
                        onClick={() => scrollToId("#contact")}
                        className="group flex items-center gap-2 shrink-0 rounded-full bg-[#F58220] hover:bg-[#E06D0C] text-white font-semibold px-7 py-3.5 text-sm transition-all duration-300 hover:shadow-[0_0_0_8px_rgba(245,130,32,0.15)]"
                    >
                        Start chapter 01
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
